import configuratorReducer from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Reducer';
import get from 'lodash/get';
import find from 'lodash/find';
import forEach from 'lodash/forEach';
import intersection from 'lodash/intersection';
import { actions } from './Actions';
import { PRODUCT } from '../../Constants/Steps';
import {
  RECEIVE_CONFIGURATION,
  RECEIVE_SWITCHOPTIONSRESULT,
  RECEIVE_OPTIONS,
  RECEIVE_SAVEDCONFIGURATION,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import { getOptionColors } from './Selectors';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

const initialState = {
  ...configuratorReducer(),
  selectedStage: PRODUCT,
  isLoading: false,
  questions: [],
  showProductParts: false,
  selectedDesign: null,
  calculationIsEnabled: false,
  showDesignView: false,
  additionalPositionsLoaded: false,
  clearYearData: false,
  availableDesigns: [],
};

const validateViewImages = (newState) => {
  let displayVisualizationNote = false;
  const itemIdentifier = get(newState, 'configuration.item.identifier', '');
  const viewImages = get(newState, 'configuration.visualizationData.viewImages', {});
  const viewIdentifiers = viewImages && Object.keys(viewImages);
  if (viewIdentifiers && viewIdentifiers.length) {
    viewIdentifiers.map((identifier) => {
      if (viewImages[identifier] === null || viewImages[identifier].length === 0) {
        viewImages[identifier] = [`/images/item/image/${itemIdentifier}.jpg`];
        displayVisualizationNote = true;
      }
    });
    return {
      ...newState,
      displayVisualizationNote,
      configuration: {
        ...newState.configuration,
        visualizationData: {
          ...newState.configuration.visualizationData,
          viewImages,
        },
      },
    };
  }
  return newState;
};

const checkRequiredValue = (value) => {
  return isNumber(value) || (isString(value) && value.length > 0);
};

const validateFeature = (feature) => {
  feature = { ...feature };
  delete feature.error;
  const error = (feature.required && !checkRequiredValue(feature.selectedOption) && 'required') || '';
  return error ? { ...feature, error } : feature;
};

function configuratorReducerBase(state = initialState, action) {
  switch (action.type) {
    case actions.SET_IS_LOADING: {
      return { ...state, isLoading: action.flag };
    }
    case actions.SET_SELECTED_STAGE: {
      return {
        ...state,
        selectedStage: action.stage,
      };
    }
    case actions.SET_NEXT_QUESTION: {
      return {
        ...state,
        questions: state.questions.concat(action.data),
      };
    }
    case actions.SET_SELECTED_DESIGN: {
      return {
        ...state,
        selectedDesign: action.design,
      };
    }
    case actions.SET_SELECTED_ANSWER: {
      const questionIndex = state.questions.findIndex((q) => q.identifier === action.identifier);
      let updatedQuestions = state.questions;
      let stateSelectedAnswers = get(state, 'configuration.customdata.selectedAnswers', []);
      let stateSeatData = get(state, 'configuration.customdata.seatData', []);
      let clearSelectedDesign = false;
      let clearYearData = false;
      if (questionIndex !== -1 && questionIndex !== state.questions.length - 1) {
        updatedQuestions = updatedQuestions.filter((question, i) => i <= questionIndex);
        if (!updatedQuestions.find((ques) => ques.identifier === 'year_start')) {
          clearYearData = true;
        }
        clearSelectedDesign = true;
      }
      stateSelectedAnswers = stateSelectedAnswers.map((q) =>
        q.identifier === action.identifier ? { ...q, selectedAnswer: action.answer } : q
      );
      updatedQuestions = updatedQuestions.map((question) =>
        question.identifier === action.identifier ? { ...question, selectedAnswer: action.answer } : question
      );

      // concat unfinished questions
      const relatedSeatData = stateSeatData.find(({ identifier }) => identifier === action.answer);
      if (relatedSeatData) {
        const lastQuestion =
          relatedSeatData.questions?.length &&
          relatedSeatData.questions[relatedSeatData.questions.length - 1];
        if (
          lastQuestion &&
          lastQuestion.identifier !== 'design' &&
          lastQuestion.selectedAnswer === undefined
        ) {
          updatedQuestions = updatedQuestions.concat(relatedSeatData.questions);
        }
      }

      if (
        stateSeatData.length > 0 &&
        updatedQuestions.findIndex((q) => q.identifier === 'seat_position') === -1
      ) {
        stateSeatData = [];
      }
      return {
        ...state,
        questions: updatedQuestions,
        selectedDesign: clearSelectedDesign ? null : state.selectedDesign,
        clearYearData,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            selectedAnswers: stateSelectedAnswers,
            seatData: stateSeatData,
          },
        },
      };
    }
    case actions.SET_QUESTIONS: {
      return {
        ...state,
        questions: action.questions,
      };
    }
    case actions.SAVE_SEAT_TYPE_DATA: {
      let seatData = get(state, 'configuration.customdata.seatData', []);
      let seatTypeData = seatData.find((s) => s.identifier === action.seatType);
      if (seatTypeData) {
        const seatDataIndex = seatData.indexOf(seatTypeData);
        const questionIndex = seatTypeData.questions.findIndex(
          (q) => q.identifier === action.question.identifier
        );
        if (questionIndex !== -1 && questionIndex < seatTypeData.questions.length) {
          seatTypeData = {
            ...seatTypeData,
            questions: seatTypeData.questions.filter((question, i) => i < questionIndex),
          };
        }
        seatTypeData = {
          ...seatTypeData,
          questions: [...seatTypeData.questions, action.question],
        };
        seatData = [...seatData];
        seatData[seatDataIndex] = seatTypeData;
      } else {
        seatData = [...seatData, { identifier: action.seatType, questions: [action.question] }];
      }
      return {
        ...state,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            seatData,
          },
        },
      };
    }
    case actions.SAVE_SEAT_DATA: {
      return {
        ...state,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            seatData: [action.data],
          },
        },
      };
    }
    case actions.SET_SHOW_DESIGN_VIEW: {
      return {
        ...state,
        showDesignView: action.flag,
      };
    }
    case actions.SET_SHOW_PRODUCT_PARTS: {
      return {
        ...state,
        showProductParts: action.flag,
      };
    }
    case RECEIVE_CONFIGURATION: {
      const seatCustomData = get(state, 'configuration.customdata.seatData', []);
      const newState = configuratorReducer(state, action);
      const modifiedState = validateViewImages(newState);
      const questions =
        modifiedState.questions.length > 0
          ? modifiedState.questions
          : get(modifiedState, 'configuration.customdata.selectedAnswers', []);
      const selectedDesign = modifiedState.selectedDesign
        ? modifiedState.selectedDesign
        : get(modifiedState, 'configuration.customdata.selectedDesign', null);
      const seatData =
        seatCustomData.length === 0
          ? get(modifiedState, 'configuration.customdata.seatData', [])
          : seatCustomData;
      return {
        ...modifiedState,
        calculationIsEnabled: true,
        questions,
        selectedDesign,
        configuration: {
          ...modifiedState.configuration,
          customdata: {
            ...modifiedState.configuration.customdata,
            selectedAnswers: questions,
            selectedDesign: selectedDesign,
            seatData,
          },
        },
      };
    }
    case actions.RESET_ADDITIONAL_POSITIONS_LOADED: {
      return {
        ...state,
        additionalPositionsLoaded: false,
      };
    }
    case RECEIVE_SWITCHOPTIONSRESULT: {
      const newState = configuratorReducer(state, action);
      const modifiedState = validateViewImages(newState);
      return modifiedState;
    }
    case actions.SET_SEAT_POSITIONS_DATA: {
      return {
        ...state,
        additionalPositionsLoaded: true,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            seatPositionsData: action.data,
          },
        },
      };
    }
    case actions.VALIDATE_SEAT_POSITIONS_DATA: {
      const seatPositionsData = get(state.configuration.customdata, 'seatPositionsData', {});
      const { seatPositions } = seatPositionsData;
      const validatedSeatPositions = seatPositions.map((seatPosition) => ({
        ...seatPosition,
        featureSelection: seatPosition.featureSelection.map(validateFeature),
      }));

      return {
        ...state,
        configuration: {
          ...state.configuration,
          customdata: {
            ...state.configuration.customdata,
            seatPositionsData: {
              ...seatPositionsData,
              seatPositions: validatedSeatPositions,
            },
          },
        },
      };
    }
    case RECEIVE_OPTIONS: {
      const data = [];
      action.data.forEach((item) => {
        if (item.colorVariants && item.colorVariants.length) {
          data.push(...item.colorVariants);
        } else {
          data.push(item);
        }
      });
      const nextState = configuratorReducer(state, { ...action, data });
      const colorFilterItems = [];
      forEach(nextState.options, (option) => {
        forEach(getOptionColors(option), (color) => {
          !find(colorFilterItems, { id: color.id }) && colorFilterItems.push(color);
        });
      });
      return {
        ...nextState,
        colorFilterItems,
      };
    }
    case RECEIVE_SAVEDCONFIGURATION: {
      const savedConfiguration = action.data;
      return {
        ...state,
        savedConfiguration,
      };
    }
  }
  return configuratorReducer(state, action);
}

export default function (state = initialState, action = {}) {
  let nextState = configuratorReducerBase(state, action);

  // update availableDesigns if seatData changed
  if (nextState.configuration.customdata?.seatData !== state.configuration.customdata?.seatData) {
    const seatData = nextState.configuration.customdata?.seatData || [];
    const availableDesigns = seatData.reduce((prev, current) => {
      const { questions } = current;
      const designQuestion = questions?.find((ques) => ques.identifier === 'design');
      if (designQuestion) {
        return prev.length ? intersection(prev, designQuestion.answers) : designQuestion.answers;
      }
      return prev;
    }, []);
    nextState = {
      ...nextState,
      availableDesigns,
    };
  }

  // update availableDesigns from questions (if availableDesigns is empty)
  if (!nextState.availableDesigns.length && nextState.questions !== state.questions) {
    const designQuestion = nextState.questions.find((ques) => ques.identifier === 'design');
    if (designQuestion) {
      nextState = {
        ...nextState,
        availableDesigns: designQuestion.answers,
      };
    }
  }

  return nextState;
}
